import React, { useEffect } from "react"
import {
  createRouter,
  createRoute,
  createRootRoute,
  Outlet,
  redirect,
  useNavigate,
} from "@tanstack/react-router"
import { useAuth } from "@/contexts/AuthContext"
import { LoginPage } from "@/pages/LoginPage"
import { ResetPasswordPage } from "@/pages/ResetPasswordPage"
import { HomePage } from "@/pages/HomePage"
import { ClinicalReviewsListPage } from "@/pages/ClinicalReviewsListPage"
import { WorkflowsListPage } from "@/pages/WorkflowsListPage"
import { SupplementalListPage } from "@/pages/SupplementalListPage"
import { DocumentViewerPage } from "@/pages/DocumentViewerPage"
import { OrgAdminPage } from "@/pages/OrgAdminPage"
import { CustomerLayout } from "@/layouts/CustomerLayout"
import { AdminLoginPage } from "@/pages/admin/AdminLoginPage"
import { AdminLayout } from "@/layouts/AdminLayout"
import { AdminDashboardPage } from "@/pages/admin/AdminDashboardPage"
import { UsersPage } from "@/pages/admin/UsersPage"
import { OrganizationDetailPage } from "@/pages/admin/OrganizationDetailPage"
import { AdminContentPage } from "@/pages/admin/AdminContentPage"
import { DocumentEditorPage } from "@/pages/admin/DocumentEditorPage"
import { AdminClinicalReviewsPage } from "@/pages/admin/AdminClinicalReviewsPage"
import { AdminWorkflowsPage } from "@/pages/admin/AdminWorkflowsPage"
import { AdminDocumentViewerPage } from "@/pages/admin/AdminDocumentViewerPage"
import { AdminSupplementalPage } from "@/pages/admin/AdminSupplementalPage"
import { QuizEditorPage } from "@/pages/admin/QuizEditorPage"
import { MyTrainingPage } from "@/pages/training/MyTrainingPage"
import { TakeQuizPage } from "@/pages/training/TakeQuizPage"
import { CertificatePage } from "@/pages/training/CertificatePage"
import { AssignmentsPage } from "@/pages/manager/AssignmentsPage"

// Role guard wrappers — redirect if the authenticated user has the wrong role.
// Uses the same useEffect + useNavigate pattern as the CustomerLayout auth guard.

// Training belongs to whoever is taking the assessment: staff, and a pharmacy
// administrator who assigned one to themselves. Gating it at the route rather
// than only on the nav link is deliberate — a link the manager cannot see is
// not a permission, and the assignment they created has to be reachable.
// Both guards also send everyone home while their organization does not have
// assessments turned on.
function StaffOrManagerGuard({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth()
  const navigate = useNavigate()
  const quizzesOff = user?.quizzes_enabled === false
  const canTakeTraining = user?.role === "staff" || user?.role === "manager"
  useEffect(() => {
    if (!isLoading && user && quizzesOff) {
      navigate({ to: "/" })
    } else if (!isLoading && user && !canTakeTraining) {
      navigate({ to: "/manage/assignments" })
    }
  }, [isLoading, user, quizzesOff, canTakeTraining, navigate])
  if (isLoading || (user && (quizzesOff || !canTakeTraining))) return null
  return <>{children}</>
}

function ManagerOnlyGuard({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth()
  const navigate = useNavigate()
  const quizzesOff = user?.quizzes_enabled === false
  useEffect(() => {
    if (!isLoading && user && quizzesOff) {
      navigate({ to: "/" })
    } else if (!isLoading && user && user.role !== "manager") {
      navigate({ to: "/training" })
    }
  }, [isLoading, user, quizzesOff, navigate])
  if (isLoading || (user && (quizzesOff || user.role !== "manager"))) return null
  return <>{children}</>
}

// Root route
const rootRoute = createRootRoute({
  component: Outlet,
})

// Customer login route (no layout)
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
  }),
})

// Password reset routes (no layout)
const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/users/password/edit",
  component: ResetPasswordPage,
})

const adminResetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/specia_admins/password/edit",
  component: ResetPasswordPage,
})

// Customer layout wrapper
const customerLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "customer",
  component: CustomerLayout,
})

// Customer routes
const homeRoute = createRoute({
  getParentRoute: () => customerLayoutRoute,
  path: "/",
  component: HomePage,
})

const clinicalReviewsRoute = createRoute({
  getParentRoute: () => customerLayoutRoute,
  path: "/clinical-reviews",
  component: ClinicalReviewsListPage,
})

const clinicalReviewViewRoute = createRoute({
  getParentRoute: () => customerLayoutRoute,
  path: "/clinical-reviews/$slug",
  component: DocumentViewerPage,
})

// The inline workflow viewer's deep-link state. TanStack parses numeric
// query values as numbers, so `activity` is coerced back to a string.
type WorkflowsSearch = { workflow?: string; clinical_review?: string; activity?: string }
const validateWorkflowsSearch = (search: Record<string, unknown>): WorkflowsSearch => {
  const str = (v: unknown) =>
    typeof v === "string" || typeof v === "number" ? String(v) : undefined
  return {
    workflow: str(search.workflow),
    clinical_review: str(search.clinical_review),
    activity: str(search.activity),
  }
}

const workflowsRoute = createRoute({
  getParentRoute: () => customerLayoutRoute,
  path: "/workflows",
  component: WorkflowsListPage,
  validateSearch: validateWorkflowsSearch,
})

// Legacy workflow viewer: workflows are now viewed inline on /workflows.
// Kept as a redirect so old bookmarks and shared #section- links keep working.
const workflowViewRoute = createRoute({
  getParentRoute: () => customerLayoutRoute,
  path: "/workflows/$slug",
  beforeLoad: ({ params, search, location }) => {
    const s = search as Record<string, string | undefined>
    const hash = location.hash?.replace(/^#/, "") || undefined
    const target: Record<string, string> = {}
    if (s.clinical_review) target.clinical_review = s.clinical_review
    if (s.activity) target.activity = String(s.activity)
    // With a section hash, omit `workflow` so the list view's deep-link
    // resolver picks the owning workflow AND activity from the section key.
    if (!hash?.startsWith("section-")) target.workflow = params.slug
    throw redirect({ to: "/workflows", search: target, hash, replace: true })
  },
})

const supplementalRoute = createRoute({
  getParentRoute: () => customerLayoutRoute,
  path: "/supplemental",
  component: SupplementalListPage,
})

const supplementalViewRoute = createRoute({
  getParentRoute: () => customerLayoutRoute,
  path: "/supplemental/$slug",
  component: DocumentViewerPage,
})

const pageViewRoute = createRoute({
  getParentRoute: () => customerLayoutRoute,
  path: "/pages/$slug",
  component: DocumentViewerPage,
})

const orgAdminRoute = createRoute({
  getParentRoute: () => customerLayoutRoute,
  path: "/settings",
  component: OrgAdminPage,
})

// Old bookmark: API Access now lives in the Admin panel
const apiAccessRoute = createRoute({
  getParentRoute: () => customerLayoutRoute,
  path: "/settings/api-access",
  beforeLoad: () => {
    throw redirect({ to: "/settings" })
  },
})

// Training routes — staff, and a manager who self-assigned
const trainingRoute = createRoute({
  getParentRoute: () => customerLayoutRoute,
  path: "/training",
  component: () => (
    <StaffOrManagerGuard>
      <MyTrainingPage />
    </StaffOrManagerGuard>
  ),
})

const takeQuizRoute = createRoute({
  getParentRoute: () => customerLayoutRoute,
  path: "/training/assessments/$assignmentId",
  component: () => (
    <StaffOrManagerGuard>
      <TakeQuizPage />
    </StaffOrManagerGuard>
  ),
})

const certificateRoute = createRoute({
  getParentRoute: () => customerLayoutRoute,
  path: "/training/certificate/$attemptId",
  component: () => (
    <StaffOrManagerGuard>
      <CertificatePage />
    </StaffOrManagerGuard>
  ),
})

// Old bookmark: quizzes were renamed to assessments
const oldTakeQuizRoute = createRoute({
  getParentRoute: () => customerLayoutRoute,
  path: "/training/quizzes/$assignmentId",
  beforeLoad: ({ params }) => {
    throw redirect({
      to: "/training/assessments/$assignmentId",
      params: { assignmentId: params.assignmentId },
    })
  },
})

// Manager routes
// The completion overview now leads the assignments page, so the old
// dashboard path redirects rather than 404ing anyone's bookmark.
const oldManageDashboardRoute = createRoute({
  getParentRoute: () => customerLayoutRoute,
  path: "/manage/dashboard",
  beforeLoad: () => {
    throw redirect({ to: "/manage/assignments" })
  },
})

const manageAssignmentsRoute = createRoute({
  getParentRoute: () => customerLayoutRoute,
  path: "/manage/assignments",
  component: () => (
    <ManagerOnlyGuard>
      <AssignmentsPage />
    </ManagerOnlyGuard>
  ),
})

// Admin login route (no layout)
const adminLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/login",
  component: AdminLoginPage,
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
  }),
})

// Admin layout wrapper
const adminLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "admin",
  component: AdminLayout,
})

// Admin routes
const adminDashboardRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin",
  component: AdminDashboardPage,
})

const adminUsersRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin/users",
  component: UsersPage,
})

const adminOrganizationRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin/organizations/$id",
  component: OrganizationDetailPage,
})

type AdminContentSearch = { tab?: "documents" | "assessments" }
const validateAdminContentSearch = (search: Record<string, unknown>): AdminContentSearch => ({
  tab: search.tab === "assessments" || search.tab === "documents" ? search.tab : undefined,
})

const adminContentRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin/content",
  component: AdminContentPage,
  validateSearch: validateAdminContentSearch,
})

const adminDocumentEditorRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin/documents/$id",
  component: DocumentEditorPage,
})

const adminClinicalReviewsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin/clinical-reviews",
  component: AdminClinicalReviewsPage,
})

const adminClinicalReviewViewRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin/clinical-reviews/$slug",
  component: AdminDocumentViewerPage,
})

const adminWorkflowsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin/workflows",
  component: AdminWorkflowsPage,
  validateSearch: validateWorkflowsSearch,
})

// Legacy admin workflow viewer: redirect to the inline list view (see
// workflowViewRoute above for the same pattern and rationale).
const adminWorkflowViewRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin/workflows/$slug",
  beforeLoad: ({ params, search, location }) => {
    const s = search as Record<string, string | undefined>
    const hash = location.hash?.replace(/^#/, "") || undefined
    const target: Record<string, string> = {}
    if (s.clinical_review) target.clinical_review = s.clinical_review
    if (s.activity) target.activity = String(s.activity)
    if (!hash?.startsWith("section-")) target.workflow = params.slug
    throw redirect({ to: "/admin/workflows", search: target, hash, replace: true })
  },
})

const adminSupplementalRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin/supplemental",
  component: AdminSupplementalPage,
})

const adminSupplementalViewRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin/supplemental/$slug",
  component: AdminDocumentViewerPage,
})

const adminPageViewRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin/pages/$slug",
  component: AdminDocumentViewerPage,
})

const oldAdminAssessmentsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin/assessments",
  beforeLoad: () => {
    throw redirect({ to: "/admin/content", search: { tab: "assessments" } })
  },
})

const adminQuizEditorRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin/assessments/$id",
  component: QuizEditorPage,
})

// Old bookmarks: quizzes were renamed to assessments
const oldAdminQuizzesRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin/quizzes",
  beforeLoad: () => {
    throw redirect({ to: "/admin/content", search: { tab: "assessments" } })
  },
})

const oldAdminQuizEditorRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin/quizzes/$id",
  beforeLoad: ({ params }) => {
    throw redirect({ to: "/admin/assessments/$id", params: { id: params.id } })
  },
})

// Route tree
const routeTree = rootRoute.addChildren([
  loginRoute,
  resetPasswordRoute,
  adminResetPasswordRoute,
  customerLayoutRoute.addChildren([
    homeRoute,
    clinicalReviewsRoute,
    clinicalReviewViewRoute,
    workflowsRoute,
    workflowViewRoute,
    supplementalRoute,
    supplementalViewRoute,
    pageViewRoute,
    orgAdminRoute,
    apiAccessRoute,
    trainingRoute,
    takeQuizRoute,
    certificateRoute,
    oldTakeQuizRoute,
    oldManageDashboardRoute,
    manageAssignmentsRoute,
  ]),
  adminLoginRoute,
  adminLayoutRoute.addChildren([
    adminDashboardRoute,
    adminUsersRoute,
    adminOrganizationRoute,
    adminContentRoute,
    adminDocumentEditorRoute,
    oldAdminAssessmentsRoute,
    adminQuizEditorRoute,
    adminClinicalReviewsRoute,
    adminClinicalReviewViewRoute,
    adminWorkflowsRoute,
    adminWorkflowViewRoute,
    adminSupplementalRoute,
    adminSupplementalViewRoute,
    adminPageViewRoute,
    oldAdminQuizzesRoute,
    oldAdminQuizEditorRoute,
  ]),
])

export const router = createRouter({ routeTree })

// Type registration for type-safe route references
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router
  }
}
